/**
 * @fileoverview The "Report a problem" sheet (B76): Grow's own bottom sheet in
 * place of Sentry's stock feedback box, which looked like another app, could not
 * take a photo on a phone and gave only English failure words.
 *
 * Mounted once in the signed-in shell (Layout); opened by the two doors, the
 * account menu's Report a problem and Today's "Let us know" (openReportProblem).
 * Sentry is only the postbox: the report goes by sendProblemReport with the
 * Grow user id already on the scope, the page, language and theme as tags and
 * an optional photo as an attachment. The logic (empty, sent, failed) is
 * reportProblem.submitReport; this draws it.
 *
 * States in the one sheet: the form; Send with nothing written (Grow's own
 * sentence, no browser bubble); sending; sent (stays until Done, with a
 * sentence on how to get a reply); failed (the hello@ words, the typed words
 * kept, never "try again").
 *
 * Foot (B111): ActionRow, Cancel left and Send right; Send waits as Button's loading (Sending...). Words: feedback.* in the
 * locale files, sitewide.cancel and sitewide.sending. Colours: theme classes only.
 * The picture is shrunk by preparePhoto, as on every screen that takes one.
 *
 * @module ReportProblemSheet
 */

import React, { useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { Camera, Check } from 'lucide-react';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useTheme } from '@/shared/hooks/useTheme';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import {
  closeReportProblem, isReportProblemOpen, subscribeReportProblem, submitReport,
} from '@/shared/lib/reportProblem';
import { sendProblemReport } from '@/shared/lib/sentry';
import { supportEmail } from '@/shared/lib/serverFailure';
import { FailureText } from '@/shared/components/ServerFailure';

/** Where the sheet is in its use. */
type Phase = 'form' | 'sending' | 'sent';

/**
 * The sheet itself. Draws nothing until a door opens it.
 */
export const ReportProblemSheet: React.FC = () => {
  const { reactTranslate: t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { pathname } = useLocation();
  const email = supportEmail();
  const open = useSyncExternalStore(subscribeReportProblem, isReportProblemOpen);
  const [message, setMessage] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [problem, setProblem] = useState<'empty' | 'failed' | 'photo' | null>(null);
  const [photoMessage, setPhotoMessage] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);

  /** Drops the picture and its preview address. */
  const clearPhoto = () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhoto(null);
    setPhotoUrl(null);
    if (picker.current) picker.current.value = '';
  };

  /** Closes the sheet; a sent report leaves it clean for the next one, an unsent one keeps its words. */
  const close = () => {
    closeReportProblem();
    if (phase === 'sent') {
      setMessage('');
      clearPhoto();
      setPhase('form');
    }
    setProblem(null);
  };

  /**
   * Takes the chosen picture through preparePhoto; a refusal shows its own sentence.
   *
   * @param file - The picture as chosen.
   */
  const choose = async (file: File | undefined) => {
    if (!file) return;
    const prepared = await preparePhoto(file);
    if (!prepared.ok) {
      setProblem('photo');
      setPhotoMessage(prepared.message);
      return;
    }
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setProblem(null);
    setPhoto(prepared.file);
    setPhotoUrl(URL.createObjectURL(prepared.file));
  };

  /** Send: Grow's own empty check, then the postbox. */
  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (phase === 'sending') return;
    setPhase('sending');
    setProblem(null);
    const outcome = await submitReport(
      { message, photo, tags: { language: i18n.language, theme, page: pathname } },
      sendProblemReport,
    );
    if (outcome === 'sent') {
      setMessage('');
      clearPhoto();
      setPhase('sent');
      return;
    }
    setPhase('form');
    setProblem(outcome);
    if (outcome === 'empty') box.current?.focus();
  };

  /** The footer line cut at the address, so the address can be a link. */
  const emailLine = t('feedback.emailLine', { email: '\u0000' }).split('\u0000');

  return (
    <BottomPanel open={open} onOpenChange={(next) => { if (!next) close(); }} title={t('feedback.title')} initialFocus={box}>
      {phase === 'sent' ? (
        <div className="space-y-4 text-center">
          <Check className="mx-auto h-8 w-8 text-verdict-good" aria-hidden="true" />
          <p className="font-semibold" role="status">{t('feedback.sentTitle')}</p>
          <p className="text-sm text-muted-foreground"><FailureText text={t('feedback.sentBody', { email })} /></p>
          <Button className="w-full" onClick={close}>{t('feedback.done')}</Button>
        </div>
      ) : (
        <form noValidate onSubmit={(event) => { void send(event); }} className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('feedback.intro')}</p>
          <div className="space-y-1.5">
            <Label htmlFor="report-problem-message">{t('feedback.messageLabel')}</Label>
            <textarea
              id="report-problem-message"
              ref={box}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={t('feedback.messagePlaceholder')}
              disabled={phase === 'sending'}
              className="w-full rounded-md border border-input bg-background p-2 text-sm outline-none focus:border-primary"
            />
            <p className="text-xs text-muted-foreground">{t('feedback.hint')}</p>
            {problem === 'empty' && <p role="alert" className="text-sm text-destructive">{t('feedback.empty')}</p>}
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={picker}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => { void choose(e.target.files?.[0]); }}
            />
            {photoUrl ? (
              <>
                <img src={photoUrl} alt="" className="h-16 w-16 rounded-md object-cover" />
                <Button type="button" variant="ghost" size="sm" onClick={clearPhoto} disabled={phase === 'sending'}>
                  {t('feedback.removePhoto')}
                </Button>
              </>
            ) : (
              <Button type="button" variant="outline" className="w-full" onClick={() => picker.current?.click()} disabled={phase === 'sending'}>
                <Camera className="mr-2 h-4 w-4" aria-hidden="true" />{t('feedback.addPhoto')}
              </Button>
            )}
          </div>
          {problem === 'photo' && <p role="alert" className="text-sm text-destructive">{photoMessage}</p>}
          {problem === 'failed' && <p role="alert" className="text-sm text-destructive"><FailureText text={t('feedback.failed', { email })} /></p>}
          <ActionRow onCancel={close} disabled={phase === 'sending'}>
            <Button type="submit" loading={phase === 'sending'} busy="sending">{t('feedback.submit')}</Button>
          </ActionRow>
          <p className="text-center text-xs text-muted-foreground">
            {emailLine[0]}
            <a href={`mailto:${email}`} className="text-primary underline underline-offset-2">{email}</a>
            {emailLine[1]}
          </p>
        </form>
      )}
    </BottomPanel>
  );
};
